import React, { memo } from 'react';
import { AdminLayout } from '../layout';
import OrderDetailModal from '../modals/OrderDetailModal';
import { PageState } from '../common';
import {
  OrderStatistics,
  OrderFilters,
  OrderListHeader,
  EmptyOrdersState,
  BulkActions,
  SelectAllCheckbox,
} from './OrderManagementComponents';
import { OrderTable } from './OrderTableComponents';
import { Order } from '../../types/order';

interface OrderManagementLayoutProps {
  // Data
  filteredOrders: Order[];
  orderStats: any;
  selectedOrderIds: number[];
  isAllSelected: boolean;
  bulkUpdating: boolean;
  loading: boolean;
  error: string | null;

  // Filters
  searchTerm: string;
  statusFilter: string;

  // Modal
  isDetailModalOpen: boolean;
  selectedOrder: Order | null;

  // Handlers
  onSearchChange: (term: string) => void;
  onStatusFilterChange: (status: string) => void;
  onBulkStatusChange: (status: string) => void;
  onBulkDelete: () => void;
  onClearSelection: () => void;
  onToggleOrder: (orderId: number) => void;
  onToggleAll: () => void;
  onViewOrder: (orderId: number) => void;
  onDeleteOrder: (orderId: number) => void;
  onStatusChange: (orderId: number, status: any) => void;
  onCloseDetailModal: () => void;
  onRetry: () => void;

  // Confirm Component
  ConfirmComponent: React.ReactNode;
}

export const OrderManagementLayout = memo<OrderManagementLayoutProps>(
  ({
    // Data
    filteredOrders,
    orderStats,
    selectedOrderIds,
    isAllSelected,
    bulkUpdating,
    loading,
    error,

    // Filters
    searchTerm,
    statusFilter,

    // Modal
    isDetailModalOpen,
    selectedOrder,

    // Handlers
    onSearchChange,
    onStatusFilterChange,
    onBulkStatusChange,
    onBulkDelete,
    onClearSelection,
    onToggleOrder,
    onToggleAll,
    onViewOrder,
    onDeleteOrder,
    onStatusChange,
    onCloseDetailModal,
    onRetry,

    // Confirm Component
    ConfirmComponent,
  }) => {
    return (
      <AdminLayout title="Sipariş Yönetimi">
        <PageState
          loading={loading}
          error={error || undefined}
          loadingStateProps={{
            size: 'lg',
            text: 'Siparişler yükleniyor...',
            variant: 'admin',
            skeleton: true,
            skeletonType: 'table',
            skeletonProps: { rows: 8, columns: 7 },
          }}
          errorStateProps={{
            title: 'Siparişler Yükleme Hatası',
            variant: 'admin',
            onRetry: onRetry,
            retryText: 'Yeniden Dene',
          }}
        >
          {/* Filtreler */}
          <OrderFilters
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            onSearchChange={onSearchChange}
            onStatusFilterChange={onStatusFilterChange}
          />

          {/* Sipariş İstatistikleri */}
          <OrderStatistics stats={orderStats} />

          {/* Toplu İşlemler */}
          <BulkActions
            selectedCount={selectedOrderIds.length}
            bulkUpdating={bulkUpdating}
            onBulkStatusChange={onBulkStatusChange}
            onBulkDelete={onBulkDelete}
            onClearSelection={onClearSelection}
          />

          {/* Sipariş Listesi */}
          <OrderListContainer
            filteredOrders={filteredOrders}
            selectedOrderIds={selectedOrderIds}
            isAllSelected={isAllSelected}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            onToggleOrder={onToggleOrder}
            onToggleAll={onToggleAll}
            onViewOrder={onViewOrder}
            onDeleteOrder={onDeleteOrder}
          />

          {/* Sipariş Detay Modal */}
          <OrderDetailModal
            isOpen={isDetailModalOpen}
            onClose={onCloseDetailModal}
            order={selectedOrder}
            onStatusChange={onStatusChange}
          />

          {/* Modern Confirm Dialog */}
          {ConfirmComponent}
        </PageState>
      </AdminLayout>
    );
  }
);

OrderManagementLayout.displayName = 'OrderManagementLayout';

interface OrderListContainerProps {
  filteredOrders: Order[];
  selectedOrderIds: number[];
  isAllSelected: boolean;
  searchTerm: string;
  statusFilter: string;
  onToggleOrder: (orderId: number) => void;
  onToggleAll: () => void;
  onViewOrder: (orderId: number) => void;
  onDeleteOrder: (orderId: number) => void;
}

const OrderListContainer = memo<OrderListContainerProps>(
  ({
    filteredOrders,
    selectedOrderIds,
    isAllSelected,
    searchTerm,
    statusFilter,
    onToggleOrder,
    onToggleAll,
    onViewOrder,
    onDeleteOrder,
  }) => (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      <OrderListHeader count={filteredOrders.length} />

      {filteredOrders.length > 0 && (
        <div className="px-6 pt-4 pb-2 border-b border-gray-100">
          <SelectAllCheckbox
            isAllSelected={isAllSelected}
            onToggleAll={onToggleAll}
            totalCount={filteredOrders.length}
          />
        </div>
      )}

      {filteredOrders.length === 0 ? (
        <EmptyOrdersState searchTerm={searchTerm} statusFilter={statusFilter} />
      ) : (
        <OrderTable
          orders={filteredOrders}
          selectedOrderIds={selectedOrderIds}
          isAllSelected={isAllSelected}
          onToggleOrder={onToggleOrder}
          onToggleAll={onToggleAll}
          onViewOrder={onViewOrder}
          onDeleteOrder={onDeleteOrder}
        />
      )}
    </div>
  )
);

OrderListContainer.displayName = 'OrderListContainer';
